import {
  memo,
  useEffect,
  useRef,
  useCallback,
  useState,
  CSSProperties
} from "react";
import useModal from "./useModal";
import { IOptionsModal } from "./models/modal.types";

// Define classes as constants to avoid typos
const OVERLAY_CLASSES = {
  BASE: "re-modal-overlay",
  OPEN: "re-modal-overlay re-modal-overlay--open",
  CLOSE: "re-modal-overlay re-modal-overlay--close"
};

const MODAL_CLASSES = {
  BASE: "re-modal",
  OPEN: "re-modal re-modal--open",
  CLOSE: "re-modal re-modal--close"
};

// Define breakpoint values
const BREAKPOINTS = {
  xs: 510,
  sm: 640,
  md: 768,
  lg: 1024,
  xl: Infinity // xl applies to anything above lg
};

// Type definition for the size prop (Align with IOptionsModal['size'] object part)
type SizeObjectOptions = {
  xs?: CSSProperties;
  sm?: CSSProperties;
  md?: CSSProperties;
  lg?: CSSProperties;
  xl?: CSSProperties;
};

const defaultStyles: CSSProperties = {
  width: "90%",
  maxWidth: "500px",
  minHeight: "500px",
  maxHeight: "500px"
};

const Dialog = () => {
  const { metaRef, onDismiss, isOpenRef, getTemplates } = useModal();
  const refModal = useRef<HTMLDialogElement>(null);
  const refOverlay = useRef<HTMLDivElement>(null);
  const animationTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const [shouldRenderContent, setShouldRenderContent] = useState(false);
  // Restore state for window width
  const [windowWidth, setWindowWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 0 // Handle SSR/initial render
  );

  // Function to apply classes safely
  const applyClass = useCallback(
    (element: HTMLElement | null, className: string) => {
      if (element) {
        element.className = className;
      }
    },
    []
  );

  const handleOpen = useCallback(() => {
    // Opening sequence
    applyClass(refOverlay.current, OVERLAY_CLASSES.OPEN);
    // Set state to render content
    setShouldRenderContent(true);

    // Apply opening class to modal after a short delay
    animationTimerRef.current = setTimeout(() => {
      applyClass(refModal.current, MODAL_CLASSES.OPEN);
    }, 50);
  }, [applyClass]);

  const handleClose = useCallback(() => {
    // Closing sequence
    applyClass(refModal.current, MODAL_CLASSES.CLOSE);

    animationTimerRef.current = setTimeout(() => {
      applyClass(refOverlay.current, OVERLAY_CLASSES.CLOSE);

      // Return to base modal state after animation
      animationTimerRef.current = setTimeout(() => {
        applyClass(refModal.current, MODAL_CLASSES.BASE);
        // Remove content when modal is fully closed
        setShouldRenderContent(false);
      }, 50);
    }, 300);
  }, [applyClass]);

  const handleBackdropClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      // Only handle clicks directly on the backdrop, not its children
      if (
        e.target === refOverlay.current &&
        !metaRef.current?.options?.disableBackdropClick
      ) {
        onDismiss();
      }
    },
    [onDismiss, metaRef]
  );

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (
        e.key === "Escape" &&
        isOpenRef.current &&
        !metaRef.current?.options?.disableEscapeKeyDown
      ) {
        onDismiss();
      }
    };

    const handleToggleEvent = (e: Event) => {
      const customEvent = e as CustomEvent;
      if (customEvent.detail?.isOpen) {
        handleOpen();
      } else {
        handleClose();
      }
    };

    // Add event listeners for keyboard and modal toggle
    document.addEventListener("keydown", handleKeyDown);
    document.addEventListener("mx-modal-toggle", handleToggleEvent);

    // --- Add Resize Listener ---
    const handleResize = () => {
      // Only update state
      setWindowWidth(window.innerWidth);
    };
    // Initial width set via useState
    handleResize(); // Set initial width correctly
    window.addEventListener("resize", handleResize);

    // Set initial classes
    applyClass(refOverlay.current, OVERLAY_CLASSES.BASE);
    applyClass(refModal.current, MODAL_CLASSES.BASE);

    return () => {
      // Clean up event listeners and timers
      document.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("mx-modal-toggle", handleToggleEvent);
      window.removeEventListener("resize", handleResize); // Clean up resize listener
      if (animationTimerRef.current) {
        clearTimeout(animationTimerRef.current);
      }
    };
  }, [applyClass, handleOpen, handleClose, onDismiss, isOpenRef, metaRef]);

  // Calculate dynamic styles based on window width
  const sizeOptions = metaRef.current?.options?.size;
  const dynamicSizeStyle =
    sizeOptions && typeof sizeOptions === "object" // Check if it's the object form
      ? getModalSizeStyle(sizeOptions as SizeObjectOptions, windowWidth)
      : {}; // Handle string case or undefined (return empty object)

  const dialogStyles: CSSProperties = {
    ...(metaRef.current?.options?.style || {}), // Base styles from options
    ...dynamicSizeStyle // Apply dynamic size styles based on width
  };

  return (
    <div
      ref={refOverlay}
      className={OVERLAY_CLASSES.BASE}
      onClick={handleBackdropClick}
    >
      <dialog
        ref={refModal}
        className={MODAL_CLASSES.BASE}
        style={dialogStyles}
        open={isOpenRef.current}
      >
        <div className="re-modal-content">
          {shouldRenderContent && getTemplates()}
        </div>
      </dialog>
    </div>
  );
};

// Updated helper function to get modal size styles based on width
// Now works with CSSProperties objects directly
function getModalSizeStyle(
  size: SizeObjectOptions,
  currentWidth: number
): CSSProperties {
  let activeStyle: CSSProperties | undefined;

  // Prefer the style for the current breakpoint, then fall back to the nearest lower one
  if (currentWidth <= BREAKPOINTS.xs) {
    activeStyle = size.xs || size.sm || size.md || size.lg || size.xl;
  } else if (currentWidth <= BREAKPOINTS.sm) {
    activeStyle = size.sm || size.xs || size.md || size.lg || size.xl;
  } else if (currentWidth <= BREAKPOINTS.md) {
    activeStyle = size.md || size.sm || size.xs || size.lg || size.xl;
  } else if (currentWidth <= BREAKPOINTS.lg) {
    // If lg is not provided, inherit from md automatically
    activeStyle = size.lg || size.md || size.sm || size.xs || size.xl;
  } else {
    // xl or above: inherit from xl, then lg, then md
    activeStyle = size.xl || size.lg || size.md || size.sm || size.xs;
  }

  // Merge with an empty base to avoid mutating originals
  return activeStyle ? { ...activeStyle } : {};
}

export default memo(Dialog);
